/* eslint-disable react/prop-types */
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment';
import Shimmer from 'react-shimmer-effect';
import IcomoonReact from 'icomoon-react';
import ReadOnlyExerciseItem from './ReadOnlyExerciseItem';
import DraggableMonthExerciseItem from './DraggableMonthExerciseItem';
import MonthAddExerciseRow from './MonthAddExerciseRow';
import SelectedItemsTextAndCheckBoxContainerWrapper from './SelectedItemsTextAndCheckBoxContainerWrapper';
import iconSet from '../../../../shared/images/teambuildr-selection.json';

const ContentContainer = styled('div')`
  display: flex;
  flex-direction: column;
  padding: 4px;
  padding-top: ${(props) => {
    if (props.noTopPadding) return '0px';
    return !props.hasBefore ? '14px' : undefined;
  }};
  flex: 1;
  /* No overflow here — MonthContainer is the single scrollable for the
   * whole month grid. Per-cell overflow-y would make rbd treat this
   * element as the closest scrollable ancestor for the Droppable and
   * miss MonthContainer scroll, leaving droppable positions stale once
   * the user scrolls during a drag. Tall days now grow their row; the
   * user scrolls MonthContainer to see everything. */
`;

const RecurringHeader = styled('div')`
  flex-direction: column;
  display: flex;
  border-radius: 4px;
  padding-top: 8px;
  padding-bottom: 8px;
  background-color: #F8F8F8;
  margin-top: ${(props) => !props.isBefore && '5px'};
`;

const IconContainerBefore = styled('div')`
  cursor: pointer;
  display: flex;
  border-radius: 4px;
`;

const IconContainer = styled('div')`
  cursor: pointer;
  display: flex;
  border-radius: 4px;
`;

const IconContainerWrapper = styled('div')`
  position: relative;
  background: #444444;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #444444;
  min-height: 25px;
  min-width: 25px;
  max-width: 25px;
  max-height: 25px;
  margin-left: 2px;
`;

const LineContainer = styled('div')`
  position: relative;
  flex-grow: 1;
  margin-left: 5px;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 2px;
    background-color: #444444;
    display: flex;
    align-items: center;
  }
  width: 100%;
  overflow: hidden;
`;

const RecurringText = styled('div')`
  margin-left: 7px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  font-size: 13px;
  flex: 1;
`;

const ChevronWrapper = styled('div')`
  margin-right: 6px;
  margin-left: 4px;
  display: flex;
  align-items: center;
  line-height: 0;
  position: relative;
  top: -2px;
  opacity: 0.5;
  transform: ${(props) => (props.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.15s ease;
`;

const RecurringHeaderRow = styled('div')`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
`;

const RecurringExpandedList = styled('div')`
  padding-top: 6px;
`;

const RecurringExerciseRow = styled('div')`
  display: flex;
  align-items: center;
  padding: 3px 4px 3px 4px;
  gap: 5px;
`;

const RecurringExerciseIcon = styled('div')`
  background: #888888;
  border-radius: 3px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 18px;
  min-width: 18px;
  max-width: 18px;
  max-height: 18px;
  flex-shrink: 0;
`;

const RecurringExerciseName = styled('div')`
  font-size: 12px;
  color: #616161;
  font-family: 'Nunito Sans';
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MonthDayContent = ({
  date,
  exercises,
  isLoading,
  droppableProvided,
  isInteractive,
}) => {
  const formatDate = (d) => `${moment(d).format('YYYY-MM-DD')}`;
  const [isBeforeExpanded, setIsBeforeExpanded] = useState(false);
  const [isAfterExpanded, setIsAfterExpanded] = useState(false);
  const beforeRecurringWorkouts = useSelector(
    (state) => state.calendar.data.beforeRecurringWorkouts,
  );
  const afterRecurringWorkouts = useSelector(
    (state) => state.calendar.data.afterRecurringWorkouts,
  );
  const athleteAddedItems = useSelector(
    (state) => state.calendar.data.calendarItems[formatDate(date)]?.athleteAddedItems,
  );
  const draggedItem = useSelector((state) => state.calendar.data.draggedItem);
  /** This day is the active (clicked/highlighted) day. Set on any click in
   * the day, independent of whether anything is selected. */
  const isActiveSelectionDay = useSelector(
    (state) => state.calendar.data.monthSelectionActiveDate === formatDate(date),
  );
  const selectedItems = useSelector((state) => state.calendar.data.selectedItems);
  /** Whether this day has any selected items. When the bar is active but
   * nothing is selected (the "Selected (0)" state) we drop the container's
   * top padding so the minimal bar sits flush. */
  const hasSelectionInThisDay = exercises.some((ex) => selectedItems[ex.assignedId]);
  /** Only show the bar on an active day that actually has items to select. */
  const showSelectionBar = isActiveSelectionDay && exercises.length > 0;

  /** When a multi-selected item is being dragged, collapse the *other*
   * selected items in this day into the dragged clone (they hide; the
   * clone shows a count badge). Mirrors itemsHiddenByDraggedBreak. */
  const draggedSelectionInfo = useMemo(() => {
    if (!draggedItem) return null;
    const selectedCount = Object.keys(selectedItems || {}).length;
    if (selectedCount <= 1) return null;
    if (!selectedItems[draggedItem.assignedId]) return null;
    const hidden = new Set();
    exercises.forEach((ex) => {
      if (String(ex.assignedId) !== String(draggedItem.assignedId)
        && selectedItems[ex.assignedId]) {
        hidden.add(String(ex.assignedId));
      }
    });
    return { hidden, count: selectedCount };
  }, [draggedItem, selectedItems, exercises]);

  /** Mirrors week view's `shouldCollapseUnderSessionBreak`
   * (DraggableDivWrapper.js:150). When a session break in this day is
   * being dragged, all items between it and the next session break (or
   * the end of the day) collapse visually so the session "follows" its
   * break. The Draggable wrappers stay mounted to preserve rbd indices —
   * only their inner content and footprint go to zero. */
  const itemsHiddenByDraggedBreak = useMemo(() => {
    if (!draggedItem || draggedItem.type !== 'session_break') return null;
    /** During a multi-select drag the selection drives what hides
     * (draggedSelectionInfo). Suppressing the break-collapse here keeps
     * unselected items under the break (e.g. a deliberately deselected
     * last item) visible and full-height, so rbd's destination.index
     * stays accurate for the chunk math. */
    if (draggedSelectionInfo) return null;
    const breakIndex = exercises.findIndex(
      (ex) => String(ex.assignedId) === String(draggedItem.assignedId),
    );
    if (breakIndex === -1) return null; /** dragged break belongs to another day */
    const hidden = new Set();
    for (let i = breakIndex + 1; i < exercises.length; i += 1) {
      if (exercises[i]?.type === 'session_break') break;
      hidden.add(String(exercises[i].assignedId));
    }
    return hidden;
  }, [draggedItem, exercises, draggedSelectionInfo]);

  const hasBefore = beforeRecurringWorkouts[formatDate(date)]
      && beforeRecurringWorkouts[formatDate(date)].length > 0;

  /** Droppable wiring: when MonthDayContent is rendered inside a
   * react-beautiful-dnd `Droppable`, the parent passes its `provided`
   * bag through `droppableProvided`. We attach innerRef + droppableProps
   * to ContentContainer and render `provided.placeholder` after the
   * draggable items so rbd can size the destination correctly during a
   * drag. */
  const containerRef = droppableProvided?.innerRef;
  const containerDroppableProps = droppableProvided?.droppableProps || {};

  if (isLoading) {
    return (
      <ContentContainer
        ref={containerRef}
        {...containerDroppableProps}
      >
        {[1, 2, 3].map((_, i) => (
          <Shimmer key={i}>
            <div style={{ height: 40, backgroundColor: '#f5f5f5', marginBottom: 4 }} />
          </Shimmer>
        ))}
        {droppableProvided?.placeholder}
      </ContentContainer>
    );
  }

  // Find the index of the first session_break
  const firstSessionBreakIndex = exercises.findIndex((ex) => ex.type === 'session_break');

  return (
    <ContentContainer
      ref={containerRef}
      hasBefore={hasBefore}
      noTopPadding={showSelectionBar && !hasSelectionInThisDay}
      {...containerDroppableProps}
    >
      {showSelectionBar && (
        <SelectedItemsTextAndCheckBoxContainerWrapper
          dayKey={formatDate(date)}
          isMonthView
        />
      )}
      {beforeRecurringWorkouts[formatDate(date)]
      && beforeRecurringWorkouts[formatDate(date)].length > 0 ? (
        <RecurringHeader isBefore>
          <RecurringHeaderRow onClick={() => setIsBeforeExpanded((v) => !v)}>
            <IconContainerBefore>
              <IconContainerWrapper>
                <IcomoonReact
                  iconSet={iconSet}
                  size={15}
                  icon='recurring-circle'
                  color='white'
                />
              </IconContainerWrapper>
              <LineContainer>
                <RecurringText>
                  Recurring (
                  {beforeRecurringWorkouts[formatDate(date)].length}
                  )
                </RecurringText>
              </LineContainer>
            </IconContainerBefore>
            <ChevronWrapper isExpanded={isBeforeExpanded}>
              <IcomoonReact iconSet={iconSet} size={10} icon='gs-arrow-down' color='#444444' />
            </ChevronWrapper>
          </RecurringHeaderRow>
          {isBeforeExpanded && (
            <RecurringExpandedList>
              {beforeRecurringWorkouts[formatDate(date)].map((ex) => (
                <RecurringExerciseRow key={ex.assignedId || ex.id}>
                  <RecurringExerciseIcon>
                    <IcomoonReact
                      iconSet={iconSet}
                      size={11}
                      icon={ex.icon === 'break' ? 'page-break' : (ex.icon || 'lift')}
                      color='white'
                    />
                  </RecurringExerciseIcon>
                  <RecurringExerciseName>{ex.title}</RecurringExerciseName>
                </RecurringExerciseRow>
              ))}
            </RecurringExpandedList>
          )}
        </RecurringHeader>
        ) : null}
      {exercises.map((exercise, index) => {
        const itemKey = `${date.format('YYYY-MM-DD')}-${exercise.assignedId || exercise.id || index}`;
        const hasSessionBreakBackground = firstSessionBreakIndex !== -1
          && index >= firstSessionBreakIndex;
        const isHiddenByDraggedBreak = !!itemsHiddenByDraggedBreak
          && itemsHiddenByDraggedBreak.has(String(exercise.assignedId));

        const isHiddenByDraggedSelection = !!draggedSelectionInfo
          && draggedSelectionInfo.hidden.has(String(exercise.assignedId));

        if (isInteractive && exercise.assignedId !== undefined) {
          return (
            <DraggableMonthExerciseItem
              key={itemKey}
              content={exercise}
              index={index}
              exercises={exercises}
              date={date}
              hasSessionBreakBackground={hasSessionBreakBackground}
              isHiddenByDraggedBreak={isHiddenByDraggedBreak}
              isHiddenByDraggedSelection={isHiddenByDraggedSelection}
              multiSelectDragCount={draggedSelectionInfo ? draggedSelectionInfo.count : 0}
            />
          );
        }

        return (
          <ReadOnlyExerciseItem
            key={itemKey}
            content={exercise}
            index={index}
            exercises={exercises}
            date={date}
            hasSessionBreakBackground={hasSessionBreakBackground}
          />
        );
      })}
      {droppableProvided?.placeholder}
      {afterRecurringWorkouts[formatDate(date)]
      && afterRecurringWorkouts[formatDate(date)].length > 0 ? (
        <RecurringHeader>
          <RecurringHeaderRow onClick={() => setIsAfterExpanded((v) => !v)}>
            <IconContainer>
              <IconContainerWrapper>
                <IcomoonReact
                  iconSet={iconSet}
                  size={15}
                  icon='recurring-circle'
                  color='white'
                />
              </IconContainerWrapper>
              <LineContainer>
                <RecurringText>
                  Recurring (
                  {afterRecurringWorkouts[formatDate(date)].length}
                  )
                </RecurringText>
              </LineContainer>
            </IconContainer>
            <ChevronWrapper isExpanded={isAfterExpanded}>
              <IcomoonReact iconSet={iconSet} size={10} icon='gs-arrow-down' color='#444444' />
            </ChevronWrapper>
          </RecurringHeaderRow>
          {isAfterExpanded && (
            <RecurringExpandedList>
              {afterRecurringWorkouts[formatDate(date)].map((ex) => (
                <RecurringExerciseRow key={ex.assignedId || ex.id}>
                  <RecurringExerciseIcon>
                    <IcomoonReact
                      iconSet={iconSet}
                      size={11}
                      icon={ex.icon === 'break' ? 'page-break' : (ex.icon || 'lift')}
                      color='white'
                    />
                  </RecurringExerciseIcon>
                  <RecurringExerciseName>{ex.title}</RecurringExerciseName>
                </RecurringExerciseRow>
              ))}
            </RecurringExpandedList>
          )}
        </RecurringHeader>
        ) : null}
      {athleteAddedItems
      && athleteAddedItems.length > 0 ? (
        <RecurringHeader>
          <IconContainer>
            <IconContainerWrapper>
              <IcomoonReact
                iconSet={iconSet}
                size={15}
                icon='user-checkmark'
                color='white'
              />
            </IconContainerWrapper>
            <LineContainer>
              <RecurringText>
                Athlete Added Items (
                {athleteAddedItems.length}
                )
              </RecurringText>
            </LineContainer>
          </IconContainer>
        </RecurringHeader>
        ) : null}
      <MonthAddExerciseRow date={date} />
    </ContentContainer>
  );
};

export default MonthDayContent;
